/* eslint import/namespace: 0 */
import * as types from "../constants/actionTypes";
import * as actions from "../actions/workOrderActions";
import * as chargeActions from "../actions/chargeActions";
import * as statusActions from "../actions/statusActions";
import workOrderDetailConfig from "./order-details-config";
import objectAssign from "object-assign";
import * as apiUrls from '../constants/apiUrls';
import {
  getFirstServiceCallId,
  getLastServiceCallId,
  hasDetailsService,
  isTroubleCallType,
} from "../utils/helper";
import { noChargeDwellingTypes } from '../constants/dwellingTypes';

export function getRequest(detailItem, orderId, state, requestModifier) {
  if (!detailItem.dependencies || !state) {
    return null;
  }

  const request = {
    orderId
  };

  const expectedDependencyCount = detailItem.dependencies.length;

  let actualDependencyCount = 0;

  detailItem.dependencies.forEach(dependency => {
    const expectedPropCount = dependency.requestProps.length;
    let actualPropCount = 0;

    dependency.requestProps.forEach(r => {
      const p = state.getIn([
        "workOrder",
        "workOrderDetails",
        orderId,
        dependency.key,
        ...r.path
      ]);

      if (p) {
        actualPropCount++;
        request[r.propName] = p;
      }
    });

    if (expectedPropCount === actualPropCount) {
      actualDependencyCount++;
    }
  });

  if (expectedDependencyCount === actualDependencyCount) {
    return objectAssign(request, requestModifier);
  }

  return null;
}

export function getOrderDetails(
  store,
  orderId,
  detailItem,
  action,
  requestModifier,
  skipLoadedCheck
) {
  const state = store.getState();

  if (
    action &&
    action.type === types.WORK_ORDER_GET_STARTED &&
    action.key === detailItem.key
  ) {
    return;
  }

  const details = state.getIn([
    "workOrder",
    "workOrderDetails",
    orderId,
    detailItem.key
  ]);
  const loading = state.getIn([
    "workOrder",
    "workOrderDetails",
    orderId,
    detailItem.key,
    "loading"
  ]);
  const loadingFinished = state.getIn([
    "workOrder",
    "workOrderDetails",
    orderId,
    detailItem.key,
    "loadingFinished"
  ]);
  const results = state.getIn([
    "workOrder",
    "workOrderDetails",
    orderId,
    detailItem.key,
    "details"
  ]);

  if (loading) {
    if (detailItem && detailItem.key === 'equipmentDetails') {
      console.log('[FST] getOrderDetails(equipmentDetails): bailed — loading=true for orderId=%s', orderId);
    }
    return;
  }

  if (details && results && (!skipLoadedCheck && loadingFinished)) {
    if (detailItem && detailItem.key === 'equipmentDetails') {
      console.log('[FST] getOrderDetails(equipmentDetails): bailed — already loaded for orderId=%s', orderId);
    }
    return;
  }

  if (!details || !results || skipLoadedCheck) {
    const request = getRequest(detailItem, orderId, state, requestModifier);
    if (detailItem && detailItem.key === 'equipmentDetails') {
      console.log('[FST] getOrderDetails(equipmentDetails): orderId=%s, request=%s, skipLoadedCheck=%s', orderId, request ? 'ok' : 'NULL — will not fire', skipLoadedCheck);
    }
    if (request) {
      store.dispatch(actions.getWorkOrderStarted(request, detailItem));
    }
  }
}

export const middlewareHandler = (store, next, action) => {
  next(action);

  if (action.type === types.WORK_ORDER_GET_TECH_ROUTE_FINISHED) {
    if (action.payload.assignedServiceOrders) {
      for (const order of action.payload.assignedServiceOrders) {
        const isOpen =
          order.serviceOrderInfoV2.serviceOrderId &&
          order.serviceOrderInfoV2.status === "open";
        getInitialOrderDetails(
          store,
          order.serviceOrderInfoV2.serviceOrderId,
          workOrderDetailConfig,
          action,
          isOpen
        );
      }
    }

    const userId = action.userId;
    if (userId) {
      store.dispatch(statusActions.getTechStatusStarted({
        techStatusChange: {
          userId,
          techStatusWorkOrder: { workOrderNumber: null, arrivalTime: null, serviceType: "" },
          techStatus: "GetStatus"
        },
        channels: [userId]
      }, userId));
    }
  }
  if (
    action.type === types.WORK_ORDER_GET_FINISHED &&
    action.key === "charges"
  ) {
    const serviceTypeId = store.getState().getIn(['workOrder', 'workOrderDetails', action.orderId, 'schedulingSystemDetails', 'details', 'serviceOrderInfoV2', 'serviceTypeId']);
    const chargeOptions = store.getState().getIn(['workOrder', 'workOrderDetails', action.orderId, 'charges']);
    const dwellingType = store.getState().getIn(['workOrder', 'workOrderDetails', action.orderId, 'customerDetails', 'details', 'dwellingType']);

    if (chargeOptions) {
      const hasServiceSpp = hasDetailsService(chargeOptions, 'spp');
      const hasServiceCallFee = hasDetailsService(chargeOptions, 'serviceCallFee');
      const serviceCallChargeId = getFirstServiceCallId(chargeOptions);
      const lastServiceCallChargeId = getLastServiceCallId(chargeOptions);

      if (hasServiceSpp && hasServiceCallFee && isTroubleCallType(serviceTypeId)) {
        store.dispatch(chargeActions.chargeQuantitySet(serviceCallChargeId, 1, action.orderId));
      }

      if (!hasServiceSpp && hasServiceCallFee) {
        store.dispatch(chargeActions.chargeQuantitySet(lastServiceCallChargeId, 1, action.orderId));
      }

      // Checks if the work order has a non-chargeable dwelling type
      if (dwellingType && hasServiceCallFee && noChargeDwellingTypes.includes(dwellingType)) {
        store.dispatch(chargeActions.chargeQuantitySet(serviceCallChargeId, 0, action.orderId));
      }
    }
  }

  if (
    action.type === types.WORK_ORDER_GET_FINISHED &&
    action.key === "orderDetails"
  ) {
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.customerDetails,
      action
    );
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.commercialDetails,
      action
    );
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.charges,
      action
    );
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.accountOtherDetails,
      action
    );
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.outletOptimizationDetails,
      action
    );
  }

  const nodeOutageParentProp = apiUrls.USE_WORKORDER_OUTAGES_LEGACY_API ? "networkDetails" : "orderDetails";
  if (
    action.type === types.WORK_ORDER_GET_FINISHED &&
    action.key === nodeOutageParentProp
  ) {
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.nodeOutage,
      action
    );
  }

  if (action.type === types.WORK_ORDER_BY_ORDER_ID_GET_FINISHED) {
    getInitialOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig,
      action,
      false
    );
    getAdvancedWorkOrderDetails(store, action);
  }

  if (action.type === types.WORK_ORDER_DETAIL_PAGE_OPENED) {
    const schedulingSystemDetails = store
      .getState()
      .getIn([
        "workOrder",
        "workOrderDetails",
        action.orderId,
        "schedulingSystemDetails"
      ]);

    store.dispatch({ type: 'SET_ACTIVELY_SELECTED_WORK_ORDER_ID', workOrderId: action.orderId });

    if (!schedulingSystemDetails) {
      store.dispatch(actions.workOrderByOrderIdGetStarted(action.orderId));
    } else {
      forceCallCustomerDetails(store, action);
      getAdvancedWorkOrderDetails(store, action);
    }
  }

  if (action.type === types.CLEAR_WORK_ORDER_DETAIL && action.key) {
    if (action.key === 'equipmentDetails') {
      console.log('[FST] Middleware CLEAR_WORK_ORDER_DETAIL: equipmentDetails cleared for orderId=%s, calling getOrderDetails', action.orderId);
      getOrderDetails(
        store,
        action.orderId,
        workOrderDetailConfig.houseEquipmentDetails,
        action,
        null,
        true
      );
    }

    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig[action.key],
      action
    );
  }

  if (
    action.type === types.SET_TECH_STATUS_FINISHED ||
    action.type === types.GET_TECH_STATUS_FINISHED
  ) {
    const statusList = action.payload.originalPayload;
    statusList.forEach(statusItem => {
      getOrderDetails(
        store,
        statusItem.workOrderNumber,
        workOrderDetailConfig.orderStatus,
        null,
        null,
        true
      );
    });
  }

  if (action.type === types.OUTLET_UPDATE_FINISHED) {
    console.log('[FST] Middleware OUTLET_UPDATE_FINISHED: orderId=%s — dispatching clearWorkOrderDetail to trigger equipment refresh', action.orderId);
    store.dispatch(actions.clearWorkOrderDetail(action.orderId, 'equipmentDetails'));
  }

  if (action.type === types.OUTLET_UPDATE_APPIAN_FINISHED) {
    console.log('[FST] Middleware OUTLET_UPDATE_APPIAN_FINISHED: orderId=%s — dispatching clearWorkOrderDetail', action.orderId);
    store.dispatch(actions.clearWorkOrderDetail(action.orderId, 'equipmentDetails'));
  }

  if (
    action.type === types.WORK_ORDER_GET_FINISHED &&
    action.key === 'equipmentDetails'
  ) {
    const pendingMessage = store.getState().getIn([
      'workOrder', 'workOrderDetails', action.orderId, 'equipmentDetails', 'outletUpdatePendingMessage'
    ]);
    console.log('[FST] Middleware WORK_ORDER_GET_FINISHED(equipmentDetails): orderId=%s, pendingMessage=%s', action.orderId, pendingMessage);
    if (pendingMessage) {
      console.log('[FST] Middleware: dispatching OUTLET_UPDATE_EQUIPMENT_REFRESHED — message=%s', pendingMessage);
      store.dispatch({ type: types.OUTLET_UPDATE_EQUIPMENT_REFRESHED, orderId: action.orderId, message: pendingMessage });
    }
  }

  if (action.type === types.EQUIPMENT_OPTION_SUBMIT_FINISHED) {
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.equipmentOptions,
      action,
      null,
      true
    );
  }
};

export const getInitialOrderDetails = (
  store,
  orderId,
  workOrderDetailConfig,
  action,
  orderIsOpen
) => {
  if (orderIsOpen) {
    getOrderDetails(store, orderId, workOrderDetailConfig.callAhead, action);
  }
  getOrderDetails(store, orderId, workOrderDetailConfig.orderDetails, action);
  getOrderDetails(store, orderId, workOrderDetailConfig.networkDetails, action);
  getOrderDetails(store, orderId, workOrderDetailConfig.repeatStatus, action);
  getOrderDetails(store, orderId, workOrderDetailConfig.closeCodes, action);
  getOrderDetails(store, orderId, workOrderDetailConfig.orderStatus, action);
  getOrderDetails(store, orderId, workOrderDetailConfig.accountOtherDetails, action);
};

export const getAdvancedWorkOrderDetails = (store, action) => {
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.billingDetails,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.collectionDetails,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.equipmentDetails,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.packageDetails,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.accountMemos,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.orderServices,
    action
  );
  getOrderDetails(store, action.orderId, workOrderDetailConfig.charges, action);
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.equipmentOptions,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.justAskDetails,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.orderCloseValidation,
    action
  );
  getOrderDetails(
    store,
    action.orderId,
    workOrderDetailConfig.houseEquipmentDetails,
    action
  );


};

export const forceCallCustomerDetails = (store, action) => {
  const state = store.getState();

  const orderDetailsLoadingFinished = state.getIn([
    "workOrder",
    "workOrderDetails",
    action.orderId,
    workOrderDetailConfig.orderDetails.key,
    "loadingFinished"
  ]);

  const orderDetailsResult = state.getIn([
    "workOrder",
    "workOrderDetails",
    action.orderId,
    workOrderDetailConfig.orderDetails.key,
    "details"
  ]);

  if (orderDetailsLoadingFinished && orderDetailsResult) {
    getOrderDetails(
      store,
      action.orderId,
      workOrderDetailConfig.customerDetails,
      action,
      null,
      true
    );
  }
};

export default function createWorkOrderMiddleware() {
  return store => next => action => {
    middlewareHandler(store, next, action);
  };
}



// WEBPACK FOOTER //
// ./src/store/workOrderMiddleware.js